<script setup lang="ts">
import { computed } from 'vue'
import JInput from '@/components/atoms/JInput.vue'
import { cn } from '@/lib/utils'

export type NumberRangeValue = { start: number | null; end: number | null }

const props = withDefaults(
  defineProps<{
    modelValue?: NumberRangeValue
    min?: number
    max?: number
    step?: number
    disabled?: boolean
    required?: boolean
    placeholder?: { start?: string; end?: string }
    separator?: string
    suffix?: string
    class?: string
    id?: string
    name?: string
  }>(),
  {
    separator: '~',
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: NumberRangeValue]
  'change': [value: NumberRangeValue]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

const current = computed<NumberRangeValue>(() => props.modelValue ?? { start: null, end: null })

const toNumber = (val: string | number): number | null => {
  if (val === '' || val === null || val === undefined) return null
  const n = Number(val)
  return isNaN(n) ? null : n
}

const handleChange = (key: 'start' | 'end', val: string | number) => {
  const next = { ...current.value, [key]: toNumber(val) }
  emit('update:modelValue', next)
  emit('change', next)
}
</script>

<template>
  <div :class="cn('flex items-center gap-1 w-full', props.class)">
    <div class="flex items-center flex-1 gap-1">
      <JInput
        :model-value="current.start ?? undefined"
        type="number"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder?.start ?? '최소'"
        class="flex-1"
        @update:model-value="handleChange('start', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
      <span v-if="suffix" class="text-muted-foreground text-xs shrink-0">{{ suffix }}</span>
    </div>
    <span class="text-muted-foreground text-xs shrink-0 select-none px-0.5">{{ separator }}</span>
    <div class="flex items-center flex-1 gap-1">
      <JInput
        :model-value="current.end ?? undefined"
        type="number"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder?.end ?? '최대'"
        class="flex-1"
        @update:model-value="handleChange('end', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
      <span v-if="suffix" class="text-muted-foreground text-xs shrink-0">{{ suffix }}</span>
    </div>
  </div>
</template>
